// The browser's half of dropping files on a spec (the sorting is shared/spec-drop.ts): walking dropped folders,
// listing what's inside dropped zips, and making the files a drop plan uploads (a Gerbers zip, a file taken out of a
// zip). fflate loads only when a zip is involved.
import type { AsyncZippable } from "fflate";
import { type DropGroup, type DroppedFile, junkFolder, zipEntryDates } from "../../../../shared/spec-drop.ts";

/** A dropped file, with where it was in a dropped folder and (zips) the names inside it. */
export type Dropped = { file: File; path?: string; entries?: string[] };

/** More than this in one drop is a whole project or disk, not an export folder. */
export const MAX_DROP_FILES = 1000;
/** Zips bigger than this aren't looked inside (they'd be read into memory whole). */
const PEEK_MAX = 150 * 1024 * 1024;

/** Whether a drag carries a folder (read during the drop event: its items are gone once it ends). */
export const hasFolder = (dt: DataTransfer) => [...dt.items].some((i) => i.kind === "file" && i.webkitGetAsEntry?.()?.isDirectory);

/**
 * The files in a drop, walking any folders (each file with its path, "ProGCC3S/gerbers/ProGCC3S-F_Cu.gtl"). Call it
 * in the drop handler itself: the entries are taken before it returns, the walking happens after.
 */
export function droppedFiles(dt: DataTransfer): Promise<Dropped[]> {
  const plain = [...dt.files].map((file) => ({ file }));
  const entries = [...dt.items].filter((i) => i.kind === "file").map((i) => i.webkitGetAsEntry?.() ?? null);
  if (!entries.some((e) => e?.isDirectory) || entries.some((e) => !e)) return Promise.resolve(plain);
  const seen = { n: 0 };
  return Promise.all(entries.map((e) => walk(e as FileSystemEntry, seen))).then((lists) => {
    if (seen.n > MAX_DROP_FILES) throw new Error(`That's over ${MAX_DROP_FILES} files: drop the export folder, not the whole project`);
    return lists.flat();
  });
}

/** A dropped folder's files, everything under it but hidden folders (.git) and backups; gives up once `seen` passes MAX_DROP_FILES. */
async function walk(entry: FileSystemEntry, seen: { n: number }): Promise<Dropped[]> {
  if (seen.n > MAX_DROP_FILES || (entry.isDirectory && junkFolder(entry.name))) return [];
  if (entry.isFile) {
    seen.n++;
    const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
    const path = entry.fullPath.replace(/^\/+/, "");
    return [{ file, path: path.includes("/") ? path : undefined }];
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const children: FileSystemEntry[] = [];
  // readEntries gives a batch at a time (100 in Chrome) until an empty one.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    children.push(...batch);
    if (seen.n + children.length > MAX_DROP_FILES) {
      seen.n = MAX_DROP_FILES + 1;
      break;
    }
  }
  const out: Dropped[] = [];
  for (const child of children) out.push(...(await walk(child, seen)));
  return out;
}

/** Files chosen with a folder picker (`webkitdirectory`) carry their path in webkitRelativePath. */
export const pickedFiles = (list: FileList | null): Dropped[] =>
  [...(list ?? [])].map((file) => ({ file, path: file.webkitRelativePath.includes("/") ? file.webkitRelativePath : undefined }));

/** The names inside each dropped zip small enough to look into (none when it can't be read), one zip at a time. */
export async function peekZips(files: Dropped[]): Promise<Dropped[]> {
  const out: Dropped[] = [];
  for (const d of files) {
    if (!/\.zip$/i.test(d.file.name) || d.file.size > PEEK_MAX) {
      out.push(d);
      continue;
    }
    try {
      const { unzipSync } = await import("fflate");
      const names: string[] = [];
      // The filter sees every entry's name and says no, so nothing is decompressed.
      unzipSync(new Uint8Array(await d.file.arrayBuffer()), {
        filter: (f) => {
          names.push(f.name);
          return false;
        },
      });
      out.push({ ...d, entries: names });
    } catch {
      out.push(d);
    }
  }
  return out;
}

/** What planDrop() reads of each dropped file. */
export const forPlan = (files: Dropped[]): DroppedFile[] => files.map((d) => ({ name: d.file.name, size: d.file.size, path: d.path, entries: d.entries }));

const TYPES: Record<string, string> = {
  csv: "text/csv",
  txt: "text/plain",
  pdf: "application/pdf",
  zip: "application/zip",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};
const typeOf = (name: string) => TYPES[name.split(".").pop()?.toLowerCase() ?? ""] ?? "";
/** A zip's entry dates must be 1980–2099; a file's own date keeps a zip of the same files byte for byte the same. */
const zipDate = (ms = 0) => (ms > Date.UTC(1980, 0, 2) && ms < Date.UTC(2099, 0, 1) ? ms : Date.UTC(1980, 0, 2));

/** The named files inside a zip, decompressed, and each one's date in it. */
async function unzipSome(file: File, names: Set<string>): Promise<{ files: Record<string, Uint8Array>; dates: Map<string, number> }> {
  const { unzip } = await import("fflate");
  const data = new Uint8Array(await file.arrayBuffer());
  const files = await new Promise<Record<string, Uint8Array>>((resolve, reject) =>
    unzip(data, { filter: (f) => names.has(f.name) }, (err, out) => (err ? reject(new Error(`Couldn't open ${file.name}: ${err.message}`)) : resolve(out))),
  );
  return { files, dates: zipEntryDates(data) };
}

async function zipAll(name: string, data: AsyncZippable): Promise<File> {
  const { zip } = await import("fflate");
  const bytes = await new Promise<Uint8Array>((resolve, reject) =>
    zip(data, { level: 6 }, (err, out) => (err ? reject(new Error(`Couldn't zip ${name}: ${err.message}`)) : resolve(out))),
  );
  return new File([bytes as Uint8Array<ArrayBuffer>], name, { type: "application/zip" });
}

/**
 * The file each group uploads, in order: a dropped file as it is, a file taken out of a dropped zip, or a zip made
 * of its parts (named as planned, each dated like its file, or like its entry in the dropped zip, never by when the
 * zip itself was downloaded; so dropping the same export again makes the same bytes and the server keeps the version
 * it has). Each dropped zip is opened once.
 */
export async function makeUploads(groups: DropGroup[], dropped: Dropped[]): Promise<File[]> {
  const wanted = new Map<number, Set<string>>();
  for (const g of groups) for (const p of g.parts) if (p.entry) wanted.set(p.file, (wanted.get(p.file) ?? new Set()).add(p.entry));
  const opened = new Map<number, Awaited<ReturnType<typeof unzipSome>>>();
  for (const [i, names] of wanted) opened.set(i, await unzipSome(dropped[i].file, names));
  const dateOf = (file: number, entry?: string) => zipDate(entry ? opened.get(file)?.dates.get(entry) : dropped[file].file.lastModified);
  const bytesOf = async (file: number, entry?: string) => {
    if (!entry) return new Uint8Array(await dropped[file].file.arrayBuffer());
    const b = opened.get(file)?.files[entry];
    if (!b) throw new Error(`${entry} is missing from ${dropped[file].file.name}`);
    return b;
  };

  const out: File[] = [];
  for (const g of groups) {
    const first = g.parts[0];
    if (!g.zip && !first.entry) out.push(dropped[first.file].file);
    else if (!g.zip) out.push(new File([(await bytesOf(first.file, first.entry)) as Uint8Array<ArrayBuffer>], g.filename, { type: typeOf(g.filename) }));
    else {
      const data: AsyncZippable = {};
      for (const p of g.parts) data[p.name] = [await bytesOf(p.file, p.entry), { mtime: dateOf(p.file, p.entry) }];
      out.push(await zipAll(g.filename, data));
    }
  }
  return out;
}
